import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * Enhanced SEO component for dynamic page metadata
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.keywords - Page keywords (comma separated)
 * @param {string} props.canonicalUrl - Canonical URL
 * @param {string} props.ogImage - Open Graph image URL
 * @param {string} props.ogType - Open Graph type (default: website)
 * @param {string} props.twitterCard - Twitter card type (default: summary_large_image)
 * @param {Array<{ href: string, hreflang: string }>} props.alternates - hreflang alternate links
 */
const SEO = ({ 
  title = 'NuWav Laboratories - Advanced Medical Testing', 
  description = 'HIPAA compliant, CAP accredited, CLIA certified laboratory offering comprehensive blood panel testing.',
  keywords = 'medical testing, laboratory services, blood panels, HIPAA compliant, CAP accredited',
  canonicalUrl = 'https://nuwavlabs.com/',
  ogImage = '/images/favicon.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noIndex = false,
  alternates = []
}) => {
  const location = useLocation();

  useEffect(() => {
    // Initialize GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // Track page view with enhanced data
    window.dataLayer.push({
      'event': 'pageview',
      'page': {
        'url': window.location.href,
        'title': title,
        'path': location.pathname,
        'search': location.search,
        'hash': location.hash
      },
      'user': {
        'language': navigator.language,
        'screenResolution': `${window.screen.width}x${window.screen.height}`,
        'viewportSize': `${window.innerWidth}x${window.innerHeight}`,
        'deviceType': /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
      },
      'timestamp': new Date().toISOString()
    });

    // Track user engagement
    const trackEngagement = () => {
      window.dataLayer.push({
        'event': 'user_engagement',
        'engagement': {
          'type': 'scroll',
          'depth': Math.round((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100)
        }
      });
    };

    // Track clicks on external links
    const trackOutboundLinks = (e) => {
      const link = e.target.closest('a');
      if (link && link.hostname !== window.location.hostname) {
        window.dataLayer.push({
          'event': 'outbound_link_click',
          'link': {
            'url': link.href,
            'text': link.textContent
          }
        });
      }
    };

    // Add event listeners
    window.addEventListener('scroll', trackEngagement);
    document.addEventListener('click', trackOutboundLinks);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', trackEngagement);
      document.removeEventListener('click', trackOutboundLinks);
    };
  }, [title, location]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      {alternates.map((alt, idx) => (
        <link key={`alt-${idx}`} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional SEO */}
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
    </Helmet>
  );
};

export default SEO; 